import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { InventoryService } from '@vex/http-service/inventory.service';

@Injectable({
  providedIn: 'root'
})
export class AssociatedConfigurationComparisonService {
  constructor(private inventoryService: InventoryService) {}

  buildFromComparisonResponse(response: any) {
    return this.buildComparisons(
      this.getConfigurationInventories(response, 'SourceConfigurationInventories'),
      this.getConfigurationInventories(response, 'TargetConfigurationInventories'),
      {
        sourceLabel: this.getVersionLabel(response, 'SourceVersion', 'Source'),
        targetLabel: this.getVersionLabel(response, 'TargetVersion', 'Target')
      }
    );
  }

  hydrateMissingCodes(comparisons: any[]): Observable<any[]> {
    const configurations = [];
    const seenInventoryIds = new Set<string>();

    comparisons.forEach((comparison) => {
      [comparison.source, comparison.target].forEach((configuration) => {
        const parsedInventoryId = this.getParsedInventoryId(configuration);
        if (!parsedInventoryId || configuration.Code) {
          return;
        }

        const inventoryId = String(parsedInventoryId);
        if (seenInventoryIds.has(inventoryId)) {
          return;
        }

        seenInventoryIds.add(inventoryId);
        configurations.push(configuration);
      });
    });

    if (!configurations.length) {
      return of(comparisons);
    }

    return forkJoin(configurations.map((configuration) =>
      this.inventoryService.getTerraformCodeById(this.getParsedInventoryId(configuration), true).pipe(
        catchError(() => of(null))
      )
    )).pipe(
      map((responses) => {
        responses.forEach((response: any, index) => {
          configurations[index].Code = response?.result || '';
        });

        comparisons.forEach((comparison) => {
          comparison.sourceCode = this.getCode(comparison.source);
          comparison.targetCode = this.getCode(comparison.target);
        });

        return comparisons;
      })
    );
  }

  getConfigurationInventories(response: any, propertyName: string) {
    return (response?.[propertyName] || []).map((configuration) => ({
      ...configuration,
      ParsedInventoryId: configuration.ParsedInventoryId || configuration.InventoryId
    }));
  }

  loadFromBlueprints(sourceBlueprint: any, targetBlueprint: any): Observable<any[]> {
    const sourceConfigurations = this.getSourcesFromBlueprint(sourceBlueprint);
    const targetConfigurations = this.getSourcesFromBlueprint(targetBlueprint);
    const labels = {
      sourceLabel: this.getVersionLabel(sourceBlueprint, 'Version', 'Source'),
      targetLabel: this.getVersionLabel(targetBlueprint, 'Version', 'Target')
    };
    const configurations = [...sourceConfigurations, ...targetConfigurations]
      .filter((configuration) => configuration.ParsedInventoryId);

    if (!configurations.length) {
      return of([]);
    }

    return forkJoin(configurations.map((configuration) =>
      this.inventoryService.getTerraformCodeById(configuration.ParsedInventoryId, true).pipe(
        catchError(() => of(null))
      )
    )).pipe(
      map((responses) => {
        responses.forEach((response: any, index) => {
          configurations[index].Code = response?.result || '';
        });

        return this.buildComparisons(sourceConfigurations, targetConfigurations, labels);
      })
    );
  }

  mergeComparisons(primaryComparisons: any[] = [], fallbackComparisons: any[] = []) {
    const mergedComparisons = [];
    const mergedByKey = new Map<string, any>();

    [...fallbackComparisons, ...primaryComparisons].forEach((comparison) => {
      const key = this.getComparisonPairKey(comparison) || `comparison-${mergedComparisons.length}`;
      const existingComparison = mergedByKey.get(key);

      if (!existingComparison) {
        const newComparison = { ...comparison };
        mergedByKey.set(key, newComparison);
        mergedComparisons.push(newComparison);
        return;
      }

      existingComparison.source = comparison.source || existingComparison.source;
      existingComparison.target = comparison.target || existingComparison.target;
      existingComparison.sourceCode = this.getCode(existingComparison.source);
      existingComparison.targetCode = this.getCode(existingComparison.target);
      existingComparison.sourceMeta = this.getMeta(existingComparison.source);
      existingComparison.targetMeta = this.getMeta(existingComparison.target);
      existingComparison.title = existingComparison.title || comparison.title;
      existingComparison.sourceLabel = comparison.sourceLabel || existingComparison.sourceLabel;
      existingComparison.targetLabel = comparison.targetLabel || existingComparison.targetLabel;
    });

    return mergedComparisons;
  }

  getSourcesFromBlueprint(blueprint: any) {
    const collections = this.getFlatBlueprintCollections(blueprint);
    const collectionSources = this.getCollectionConfigurationSources(collections);
    const collectionSourceById = new Map<string, any>();
    collectionSources.forEach((source) => {
      const collectionId = source?.BlueprintCollectionId || source?.Id;
      if (collectionId) {
        collectionSourceById.set(String(collectionId), source);
      }
    });

    const templateSources = (blueprint?.TemplateConfigurationSources || [])
      .map((source) => {
        const blueprintCollectionId = source?.BlueprintCollectionId;
        const collectionSource = blueprintCollectionId
          ? collectionSourceById.get(String(blueprintCollectionId))
          : null;
        const parsedInventoryId = source?.ParsedInventoryId || collectionSource?.ParsedInventoryId;
        return {
          ...collectionSource,
          ...source,
          ParsedInventoryId: parsedInventoryId,
          BlueprintCollectionId: blueprintCollectionId || collectionSource?.BlueprintCollectionId,
          CollectionName: collectionSource?.CollectionName,
          CollectionEntryName: collectionSource?.CollectionEntryName,
          Code: ''
        };
      });

    const seen = new Set<string>();
    return [...templateSources, ...collectionSources].filter((source) => {
      const parsedInventoryId = source?.ParsedInventoryId;
      if (!parsedInventoryId || seen.has(parsedInventoryId)) {
        return false;
      }
      seen.add(parsedInventoryId);
      source.ParsedInventoryId = parsedInventoryId;
      source.Code = source.Code || '';
      return true;
    });
  }

  getFlatBlueprintCollections(blueprint: any) {
    const collections = [];
    (blueprint?.BlueprintContainers || []).forEach((container) => {
      (container.BlueprintCollections || []).forEach((collection) => {
        this.addFlatBlueprintCollection(collections, collection);
      });
    });
    return collections;
  }

  addFlatBlueprintCollection(collections: any[], collection: any) {
    if (!collection) {
      return;
    }

    collections.push(collection);
    [
      ...(collection.BlueprintChildCollections || []),
      ...(collection.BlueprintCollections || []),
      ...(collection.Collections || [])
    ].forEach((child) => {
      this.addFlatBlueprintCollection(collections, child);
    });
  }

  getCollectionConfigurationSources(collections: any[] = []) {
    return collections
      .map((collection) => {
        const parsedInventoryId = collection?.SourceParsedInventoryId;
        if (!parsedInventoryId) {
          return null;
        }

        const collectionId = collection?.Id;

        return {
          ...collection,
          ParsedInventoryId: parsedInventoryId,
          BlueprintCollectionId: collectionId,
          AttachmentId: collection?.SourceAttachmentId,
          AttachmentPath: collection?.SourceAttachmentPath,
          TemplateId: collection?.TemplateId || collection?.TemplateCreatorID,
          CollectionName: collection.CollectionName,
          CollectionEntryName: this.getCollectionEntryTitle(collection)
        };
      })
      .filter(Boolean);
  }

  getCollectionEntryTitle(collection: any) {
    const attributes = collection?.BlueprintCollectionAttributes || [];
    const nameAttribute = attributes.find((attribute) => attribute.Name?.toLowerCase() === 'name');
    const titleAttribute = nameAttribute || attributes[0];
    return titleAttribute?.Value || collection?.CollectionEntryName || collection?.CollectionName;
  }

  buildComparisons(sourceConfigurations: any[] = [], targetConfigurations: any[] = [], labels: any = {}) {
    const comparisons = [];
    const targetByKey = new Map<string, any[]>();

    targetConfigurations.forEach((configuration) => {
      const key = this.getComparisonKey(configuration);
      if (!targetByKey.has(key)) {
        targetByKey.set(key, []);
      }
      targetByKey.get(key).push(configuration);
    });

    sourceConfigurations.forEach((sourceConfiguration, index) => {
      const key = this.getComparisonKey(sourceConfiguration);
      const matchingTargets = targetByKey.get(key) || [];
      const targetConfiguration = matchingTargets.shift();
      comparisons.push(this.createComparison(sourceConfiguration, targetConfiguration, index, labels));
    });

    targetByKey.forEach((remainingTargets) => {
      remainingTargets.forEach((targetConfiguration) => {
        comparisons.push(this.createComparison(null, targetConfiguration, comparisons.length, labels));
      });
    });

    return comparisons;
  }

  createComparison(sourceConfiguration: any, targetConfiguration: any, index: number, labels: any = {}) {
    const displaySource = sourceConfiguration || targetConfiguration || {};
    return {
      title: this.getTitle(displaySource, index),
      source: sourceConfiguration,
      target: targetConfiguration,
      sourceLabel: labels.sourceLabel || this.getVersionLabel(sourceConfiguration, 'Version', 'Source'),
      targetLabel: labels.targetLabel || this.getVersionLabel(targetConfiguration, 'Version', 'Target'),
      sourceCode: this.getCode(sourceConfiguration),
      targetCode: this.getCode(targetConfiguration),
      sourceMeta: this.getMeta(sourceConfiguration),
      targetMeta: this.getMeta(targetConfiguration)
    };
  }

  getComparisonPairKey(comparison: any) {
    return this.getComparisonKey(comparison?.source || comparison?.target);
  }

  getComparisonKey(configuration: any) {
    if (!configuration) {
      return '';
    }

    const blueprintCollectionId = configuration.BlueprintCollectionId;

    if (blueprintCollectionId) {
      return ['collection', blueprintCollectionId].join('|');
    }

    const templateId = configuration.TemplateId;
    const entryName = this.normalizeKey(configuration.CollectionEntryName || configuration.CollectionName);

    if (templateId && entryName) {
      return ['template-entry', templateId, entryName].join('|');
    }

    if (templateId) {
      return ['template', templateId].join('|');
    }

    return [
      configuration.AttachmentId || configuration.AttachmentPath,
      configuration.ParsedInventoryId
    ].filter(Boolean).join('|');
  }

  normalizeKey(value: any) {
    return (value || '').toString().trim().toLowerCase();
  }

  getTitle(configuration: any, index: number) {
    return configuration?.CollectionEntryName || `Associated configuration ${index + 1}`;
  }

  getVersionLabel(source: any, propertyName: string, fallback: string) {
    const versionValue = source?.[propertyName];
    return versionValue != null ? `v${versionValue}` : fallback;
  }

  getCode(configuration: any) {
    if (!configuration) {
      return 'No associated configuration found for this side.';
    }

    return configuration.Code || 'No generated configuration available.';
  }

  getMeta(configuration: any) {
    if (!configuration) {
      return 'Not available';
    }

    const nameValue = configuration.CollectionEntryName || configuration.CollectionName;
    const inventoryValue = this.getParsedInventoryId(configuration);
    const templateValue = configuration.TemplateId;
    const name = nameValue ? `Name: ${nameValue}` : null;
    const inventoryId = inventoryValue ? `Inventory: ${inventoryValue}` : null;
    const templateId = templateValue ? `Template: ${templateValue}` : null;
    return [name, inventoryId, templateId].filter(Boolean).join(' | ') || 'Parsed configuration';
  }

  getParsedInventoryId(configuration: any) {
    return configuration?.ParsedInventoryId;
  }
}
